import { DOCUMENT } from '@angular/common'
import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, Injector, createComponent, inject, signal } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Observable, Subject } from 'rxjs'
import { filter, skip, take } from 'rxjs/operators'
import { WizardConfig, WizardHeaderActions, WizardOpenResult } from '../types/wizard.type'
import { WizardComponent } from '../wizard.component'

type RouteWizardRegistration = {
  hasUnsavedChanges: () => boolean
  cancelPendingBlurSave?: () => void
  unsavedModalId: string
}

type WizardContentLayer = {
  componentRef: ComponentRef<any>
  onClose: Subject<void>
  breadcrumbs: string[]
}

@Injectable({ providedIn: 'root' })
export class WizardService {
  /** Runtime header actions for the top overlay layer (e.g. persona editor Save / feature toggle). */
  public readonly headerActions = signal<WizardHeaderActions | null>(null)

  private readonly _openWizardZIndex = '49'
  private readonly _closedWizardZIndex = '-1'
  private _wizardRef?: ComponentRef<WizardComponent>
  private _layers: WizardContentLayer[] = []
  private _wizardHost?: HTMLElement
  private _previousOverflow = ''
  private _isClosingWizard = false
  private _routeWizardRegistration?: RouteWizardRegistration
  private _pendingRouteCloseCallback: (() => void) | null = null
  private readonly _document = inject(DOCUMENT)
  private readonly _modalService = inject(ModalService)
  private readonly _authService = inject(AuthService)

  constructor(private _appRef: ApplicationRef, private _env: EnvironmentInjector, private _injector: Injector, private _router: Router) {
    this._closeWizardOnNavigationAwayFromEventsList()
    this._closeWizardOnOrganizationSwitch()
  }

  /** Single wizard reference guard for Escape handling. */
  public isTopOverlayWizard(instance: WizardComponent): boolean {
    return this._wizardRef?.instance === instance
  }

  public setHeaderActions(actions: WizardHeaderActions | null): void {
    this.headerActions.set(actions)
  }

  /**
   * @description Open the wizard
   * @param config - The configuration for the wizard
   * @returns The wizard open result
   */
  public open<T>(config: WizardConfig<T>): WizardOpenResult<T> {
    const breadcrumbs = config?.headerConfig?.breadcrumbs || []

    const wizardRef = this._wizardRef ?? this._createWizardShell()

    if (config.replaceTop && this._layers.length > 0) {
      const componentRef = this._replaceTop(config, wizardRef, breadcrumbs)
      const topLayer = this._layers[this._layers.length - 1]
      return {
        wizardRef,
        componentRef,
        close: () => this._wizardRef?.instance.close(),
        onClose: topLayer.onClose
      }
    }

    const onClose = new Subject<void>()
    const componentRef = this._attach(config, wizardRef, onClose, breadcrumbs)

    return {
      wizardRef: wizardRef,
      componentRef,
      close: () => this._wizardRef?.instance.close(),
      onClose
    }
  }

  /**
   * Swap top layer content in place so close still pops this slot (not a prior stacked view).
   */
  private _replaceTop<T>(config: WizardConfig<T>, wizardRef: ComponentRef<WizardComponent>, breadcrumbs: string[]): ComponentRef<T> {
    const topLayer = this._layers[this._layers.length - 1]
    this.setHeaderActions(null)
    topLayer.componentRef.destroy()

    const componentRef = wizardRef.instance.contentHost.createComponent(config.component, { injector: this._injector })
    this._applyComponentInputs(componentRef, config.componentInputs)

    topLayer.componentRef = componentRef
    topLayer.breadcrumbs = breadcrumbs
    wizardRef.setInput('breadcrumbs', breadcrumbs)
    wizardRef.setInput('contentRef', componentRef)
    wizardRef.changeDetectorRef.detectChanges()
    return componentRef
  }

  /**
   * @description Create the wizard shell
   * @returns {ComponentRef<WizardComponent>} The wizard shell component reference
   */
  private _createWizardShell(): ComponentRef<WizardComponent> {
    this._previousOverflow = this._document.body.style.overflow
    this._document.body.style.overflow = 'hidden'

    const wizardRef = createComponent(WizardComponent, { environmentInjector: this._env })
    this._wizardRef = wizardRef

    this._wizardHost = this._document.getElementById('wizard__container') ?? this._document.body
    this._wizardHost.style.zIndex = this._openWizardZIndex
    this._wizardHost.style.pointerEvents = 'auto'

    const domElem = wizardRef.location.nativeElement as HTMLElement
    this._wizardHost.appendChild(domElem)

    this._appRef.attachView(wizardRef.hostView)
    wizardRef.changeDetectorRef.detectChanges()

    wizardRef.instance.onClose.subscribe(() => this._closeTopLayerOrWizard())

    return wizardRef
  }

  /**
   * Signal inputs reject property assignment. `setInput` also covers decorator inputs.
   */
  private _applyComponentInputs<T>(componentRef: ComponentRef<T>, inputs: Partial<T> | undefined): void {
    if (!inputs) return

    for (const [key, value] of Object.entries(inputs)) componentRef.setInput(key, value)
  }

  /**
   * @description Attach the layer to the wizard
   * @param config - The configuration for the layer
   * @param wizardRef - The wizard component reference
   * @param onClose - The subject for the on close event
   * @param breadcrumbs - The breadcrumbs for the layer
   * @returns {ComponentRef<T>} The layer component reference
   */
  private _attach<T>(config: WizardConfig<T>, wizardRef: ComponentRef<WizardComponent>, onClose: Subject<void>, breadcrumbs: string[]): ComponentRef<T> {
    const previousLayer = this._layers[this._layers.length - 1]
    if (previousLayer) {
      ;(previousLayer.componentRef.location.nativeElement as HTMLElement).style.display = 'none'
    }

    const componentRef = wizardRef.instance.contentHost.createComponent(config.component, { injector: this._injector })
    this._applyComponentInputs(componentRef, config.componentInputs)
    this._layers.push({ componentRef, onClose, breadcrumbs })
    wizardRef.setInput('breadcrumbs', breadcrumbs)
    wizardRef.setInput('contentRef', componentRef)
    wizardRef.changeDetectorRef.detectChanges()
    return componentRef
  }

  /**
   * @description Close the top layer or the wizard
   */
  private _closeTopLayerOrWizard(): void {
    const topLayer = this._layers[this._layers.length - 1]
    if (!topLayer || !this._wizardRef) return

    if (this._layers.length > 1) {
      this._layers.pop()
      topLayer.onClose.next()
      topLayer.onClose.complete()
      topLayer.componentRef.destroy()

      const previousLayer = this._layers[this._layers.length - 1]
      ;(previousLayer.componentRef.location.nativeElement as HTMLElement).style.display = ''
      this._wizardRef.setInput('contentRef', previousLayer.componentRef)
      this._wizardRef.setInput('breadcrumbs', previousLayer.breadcrumbs)
      this._wizardRef.changeDetectorRef.detectChanges()
      return
    }

    if (this._isClosingWizard) return
    this._isClosingWizard = true

    const wizardRef = this._wizardRef
    const host = this._wizardHost
    if (!host) {
      this._document.body.style.overflow = this._previousOverflow
      this._isClosingWizard = false
      return
    }

    wizardRef.location.nativeElement.addEventListener(
      'animationend',
      () => {
        this._layers = []
        topLayer.onClose.next()
        topLayer.onClose.complete()
        topLayer.componentRef.destroy()
        host.style.zIndex = this._closedWizardZIndex
        host.style.pointerEvents = 'none'
        this._document.body.style.overflow = this._previousOverflow
        try {
          this._appRef.detachView(wizardRef.hostView)
        } finally {
          wizardRef.destroy()
          this._wizardRef = undefined
          this._wizardHost = undefined
          this._isClosingWizard = false
          this.setHeaderActions(null)
        }
      },
      { once: true }
    )
    wizardRef.location.nativeElement.classList.add('wizard-closing')
  }

  /**
   * @description Update the wizard header breadcrumbs at runtime
   * @param breadcrumbs - New breadcrumbs array
   */
  public setBreadcrumbs(breadcrumbs: string[]): void {
    if (!this._wizardRef || this._layers.length === 0) return
    this._layers[this._layers.length - 1].breadcrumbs = breadcrumbs
    this._wizardRef.setInput('breadcrumbs', breadcrumbs)
    this._wizardRef.changeDetectorRef.detectChanges()
  }

  /**
   * Register a route-based wizard so close requests from navbar can be delegated
   * when there is no overlay wizard instance.
   */
  public registerRouteWizard(registration: RouteWizardRegistration): () => void {
    this._routeWizardRegistration = registration
    return () => {
      if (this._routeWizardRegistration === registration) {
        this._routeWizardRegistration = undefined
        this._pendingRouteCloseCallback = null
      }
    }
  }

  /**
   * If the wizard is open, trigger its close flow (shows unsaved modal if needed).
   * When the wizard actually closes, runs the callback (e.g. navigate home).
   * For route-based wizards (e.g. event wizard on /update/event/:id), delegates to the registered route wizard.
   */
  public requestCloseAndThen(callback?: (() => void) | null): void {
    if (this._wizardRef) {
      const closeCurrentLayer = (): void => {
        const currentRef = this._wizardRef
        const currentLayer = this._layers[this._layers.length - 1]
        if (!currentRef || !currentLayer) {
          callback?.()
          return
        }
        currentLayer.onClose.pipe(take(1)).subscribe(() => {
          if (this._layers.length > 0) {
            closeCurrentLayer()
          } else {
            callback?.()
          }
          if (this._layers.length > 1) {
            closeCurrentLayer()
            return
          }
          const waitForTeardown = (): void => {
            if (this._wizardRef) {
              setTimeout(waitForTeardown, 0)
            } else {
              callback?.()
            }
          }
          waitForTeardown()
        })
        currentRef.instance.close()
      }

      closeCurrentLayer()
      return
    }
    if (this._routeWizardRegistration) {
      if (!this._routeWizardRegistration.hasUnsavedChanges()) {
        callback?.()
        return
      }
      this._routeWizardRegistration.cancelPendingBlurSave?.()
      this._pendingRouteCloseCallback = callback ?? null
      const modalId = this._routeWizardRegistration.unsavedModalId
      if (!this._modalService.isModalOpen(modalId)) this._modalService.toggleModal(modalId)
      return
    }
    callback?.()
  }

  /** Save pending route wizard changes, then run deferred navigation callback. */
  public confirmRouteCloseWithSave(saveChanges?: () => Observable<void>): Observable<void> {
    return new Observable<void>((subscriber) => {
      if (!saveChanges) {
        this._closeRouteUnsavedModalIfOpen()
        this._runPendingRouteCloseCallback()
        subscriber.next()
        subscriber.complete()
        return
      }

      saveChanges()
        .pipe(take(1))
        .subscribe({
          next: () => {
            this._closeRouteUnsavedModalIfOpen()
            this._runPendingRouteCloseCallback()
            subscriber.next()
            subscriber.complete()
          },
          error: (error) => subscriber.error(error)
        })
    })
  }

  /** Discard pending route wizard changes and run deferred navigation callback. */
  public discardRouteClose(): void {
    this._closeRouteUnsavedModalIfOpen()
    this._runPendingRouteCloseCallback()
  }

  /** User dismissed route unsaved modal (X/backdrop/escape): keep page, cancel deferred navigation. */
  public dismissRouteClose(): void {
    this._pendingRouteCloseCallback = null
  }

  private _runPendingRouteCloseCallback(): void {
    const callback = this._pendingRouteCloseCallback
    this._pendingRouteCloseCallback = null
    callback?.()
  }

  private _closeRouteUnsavedModalIfOpen(): void {
    const modalId = this._routeWizardRegistration?.unsavedModalId
    if (!modalId) return
    if (this._modalService.isModalOpen(modalId)) this._modalService.toggleModal(modalId)
  }

  /**
   * Close the wizard on navigation away from the events list
   */
  private _closeWizardOnNavigationAwayFromEventsList(): void {
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      if (event.urlAfterRedirects !== '/events/list') this.closeAllWizardLayers()
    })
  }

  /**
   * Close the wizard when the active organization changes (e.g. org switcher).
   */
  private _closeWizardOnOrganizationSwitch(): void {
    this._authService.currentWorkspaceChange.pipe(skip(1)).subscribe(() => this.closeAllWizardLayers())
  }

  /**
   * Close all wizard layers
   */
  public closeAllWizardLayers(): void {
    this._document.body.style.overflow = this._previousOverflow
    this._isClosingWizard = true
    this._layers.forEach((layer) => {
      layer.onClose.next()
      layer.onClose.complete()
      layer.componentRef.destroy()
    })
    this._layers = []
    this._isClosingWizard = false
    this._wizardRef?.destroy()
    if (this._wizardHost) {
      this._wizardHost.style.zIndex = this._closedWizardZIndex
      this._wizardHost = undefined
    }
    this._wizardRef = undefined
    this._isClosingWizard = false
    this.setHeaderActions(null)
  }
}
